import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DIVISIONS } from "@/data/divisions";
import { CAREER_FORM } from "@/constants/testIds";

const POSITIONS = [
  "Offshore Engineer",
  "Subsea Technician",
  "ROV Pilot",
  "Diver (Saturation)",
  "Rig Mechanic",
  "Crane Operator",
  "Mud Logger",
  "Cybersecurity Analyst",
  "Digital Twin Specialist",
  "QHSE Officer",
  "Marine Radio Operator",
  "Offshore Medic",
  "Metocean Forecaster",
  "Cementing Engineer",
  "Rope Access Technician",
  "Other",
];

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  position: "",
  division_preference: "",
  years_experience: 0,
  certifications: "",
  cover_letter: "",
  resume_url: "",
};

export default function Careers() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/careers", {
        ...form,
        years_experience: Number(form.years_experience) || 0,
      });
      setDone(true);
      setForm(INITIAL);
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-32 text-center">
        <div className="inline-flex w-16 h-16 bg-[#FF5722] items-center justify-center mb-6">
          <CheckCircle2 size={30} className="text-white" />
        </div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-4">Application submitted.</h2>
        <p className="text-white/70 max-w-xl mx-auto">
          Our centralized HR team reviews every submission within 5 business days and will reach
          out via the email you provided.
        </p>
        <button
          className="btn-secondary mt-8"
          onClick={() => setDone(false)}
          data-testid="career-submit-another"
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <div className="text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-14">
          <div className="eyebrow mb-6">Group Careers</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            Build the deck.<br />
            <span className="text-[#FF5722]">Work every ocean.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            A single Group contract. Deployable across every subsidiary. Every ocean. Fill in the
            form below to open a conversation with our HR office.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
        <form onSubmit={submit} className="border border-white/10 bg-[#0C1322] p-8 lg:p-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Full name *" testId={CAREER_FORM.fullName}>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid={CAREER_FORM.fullName} className={inputCls} />
          </Field>
          <Field label="Email *" testId={CAREER_FORM.email}>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={CAREER_FORM.email} className={inputCls} />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid={CAREER_FORM.phone} className={inputCls} />
          </Field>
          <Field label="Years of experience *">
            <input required type="number" min={0} max={70} value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid={CAREER_FORM.yearsExp} className={inputCls} />
          </Field>
          <Field label="Position of interest *">
            <select required value={form.position} onChange={(e) => set("position", e.target.value)} data-testid={CAREER_FORM.position} className={inputCls}>
              <option value="">Select a role</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Division preference">
            <select value={form.division_preference} onChange={(e) => set("division_preference", e.target.value)} data-testid={CAREER_FORM.divisionPref} className={inputCls}>
              <option value="">No preference</option>
              {DIVISIONS.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Certifications (BOSIET, OGUK, etc.)" full>
            <input value={form.certifications} onChange={(e) => set("certifications", e.target.value)} data-testid={CAREER_FORM.certifications} className={inputCls} />
          </Field>
          <Field label="Resume URL (Google Drive, Dropbox, LinkedIn)" full>
            <input value={form.resume_url} onChange={(e) => set("resume_url", e.target.value)} data-testid={CAREER_FORM.resumeUrl} className={inputCls} placeholder="https://..." />
          </Field>
          <Field label="Cover letter *" full>
            <textarea required rows={6} minLength={10} value={form.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} data-testid={CAREER_FORM.coverLetter} className={inputCls} placeholder="Tell us about your offshore experience, motivations, and any specific rigs, regions or projects you've worked on..." />
          </Field>

          <div className="md:col-span-2 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between pt-4 border-t border-white/10">
            <div className="text-xs text-[#94A3B8] font-mono uppercase tracking-widest">
              Submitted applications route to Group HR (centralized recruitment).
            </div>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid={CAREER_FORM.submit}>
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#060A14] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
